import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Funnel, Plus, Trash, Warning, X } from "@phosphor-icons/react";
import { Button, EthiopianDatePicker, Notification } from "components/UI";
import { useChurchStore } from "features/churches";
import { useMessagesStore } from "features/messages";
import { useAddTeamMember } from "features/teams";
import { isEqual } from "lodash";
import queryString from "query-string";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useCentralStore } from "stores";
import {
  useCities,
  useCountries,
  useRegions,
  useSubcities,
  useWoredas,
  useZones
} from "../api";
import {
  AddTeamModal,
  ApplicationRequestsTable,
  ColumnsVisibilityModal,
  ExportModal,
  InactiveMembersTable,
  KidsTable,
  MembersTable,
  MembersTableHeader,
  PartnersTable
} from "../components";
import { FILTER_OPERATIONS, MEMBER_STATES, TAB_NAMES } from "../constants";
import kidsFilterFields from "../data/kidsFilterFields.json";
import membersFilterFields from "../data/membersFilterFields.json";
import ministryPreferences from "../data/ministryPreferences.json";
import partnersFilterFields from "../data/partnersFilterFields.json";
import { useMembersStore } from "../stores";

const OPERATOR_LABEL_KEYS = {
  [FILTER_OPERATIONS.EQUAL]: "members.filterBuilder.op.eq",
  [FILTER_OPERATIONS.NOT_EQUAL]: "members.filterBuilder.op.ne",
  [FILTER_OPERATIONS.LIKE]: "members.filterBuilder.op.like",
  [FILTER_OPERATIONS.GREATER_THAN]: "members.filterBuilder.op.gt",
  [FILTER_OPERATIONS.LESS_THAN]: "members.filterBuilder.op.lt",
  [FILTER_OPERATIONS.GREATER_THAN_OR_EQUAL]: "members.filterBuilder.op.gte",
  [FILTER_OPERATIONS.LESS_THAN_OR_EQUAL]: "members.filterBuilder.op.lte",
  [FILTER_OPERATIONS.BETWEEN]: "members.filterBuilder.op.between",
  [FILTER_OPERATIONS.IS_NULL]: "members.filterBuilder.op.is_null",
  [FILTER_OPERATIONS.IS_NOT_NULL]: "members.filterBuilder.op.is_not_null"
};

const LOCATION_LEVEL_LABEL_KEYS = {
  country: "members.filterBuilder.country",
  region: "members.filterBuilder.region",
  zone: "members.filterBuilder.zone",
  city: "members.filterBuilder.city",
  sub_city: "members.filterBuilder.subCity",
  woreda: "members.filterBuilder.woreda"
};

const OPERATORS_BY_TYPE = {
  string: [
    FILTER_OPERATIONS.LIKE,
    FILTER_OPERATIONS.EQUAL,
    FILTER_OPERATIONS.NOT_EQUAL,
    FILTER_OPERATIONS.IS_NULL,
    FILTER_OPERATIONS.IS_NOT_NULL
  ],
  enum: [
    FILTER_OPERATIONS.EQUAL,
    FILTER_OPERATIONS.NOT_EQUAL,
    FILTER_OPERATIONS.IS_NULL,
    FILTER_OPERATIONS.IS_NOT_NULL
  ],
  location: [
    FILTER_OPERATIONS.EQUAL,
    FILTER_OPERATIONS.NOT_EQUAL,
    FILTER_OPERATIONS.IS_NULL,
    FILTER_OPERATIONS.IS_NOT_NULL
  ],
  number: [
    FILTER_OPERATIONS.EQUAL,
    FILTER_OPERATIONS.NOT_EQUAL,
    FILTER_OPERATIONS.GREATER_THAN,
    FILTER_OPERATIONS.LESS_THAN,
    FILTER_OPERATIONS.GREATER_THAN_OR_EQUAL,
    FILTER_OPERATIONS.LESS_THAN_OR_EQUAL,
    FILTER_OPERATIONS.BETWEEN,
    FILTER_OPERATIONS.IS_NULL,
    FILTER_OPERATIONS.IS_NOT_NULL
  ],
  date: [
    FILTER_OPERATIONS.EQUAL,
    FILTER_OPERATIONS.NOT_EQUAL,
    FILTER_OPERATIONS.GREATER_THAN,
    FILTER_OPERATIONS.LESS_THAN,
    FILTER_OPERATIONS.GREATER_THAN_OR_EQUAL,
    FILTER_OPERATIONS.LESS_THAN_OR_EQUAL,
    FILTER_OPERATIONS.BETWEEN,
    FILTER_OPERATIONS.IS_NULL,
    FILTER_OPERATIONS.IS_NOT_NULL
  ]
};

const VALUELESS_OPERATORS = new Set([
  FILTER_OPERATIONS.IS_NULL,
  FILTER_OPERATIONS.IS_NOT_NULL
]);

// Operators available for a field, honoring an optional per-field
// `excludeOperators` override (e.g. numeric counts that are never null and so
// must not offer the "is empty" / "is not empty" operators).
const getOperatorsForField = def => {
  const ops = OPERATORS_BY_TYPE[def?.type] || [];
  if (!def?.excludeOperators?.length) return ops;
  const excluded = new Set(def.excludeOperators);
  return ops.filter(op => !excluded.has(op));
};

// Enum fields may defer their options to a shared data file via `optionsFrom`
// instead of inlining them, so long option lists stay in one place and keep
// their translations.
const OPTION_SOURCES = {
  ministryPreferences: t =>
    ministryPreferences.flatMap(group =>
      group.options.map(option => ({
        label: `${t(group.name)} \u2013 ${t(option.name)}`,
        value: option.value
      }))
    )
};

const resolveFieldOptions = (schema, t) =>
  Object.fromEntries(
    Object.entries(schema).map(([key, def]) =>
      def.optionsFrom
        ? [key, { ...def, options: OPTION_SOURCES[def.optionsFrom]?.(t) ?? [] }]
        : [key, def]
    )
  );

const generateID = () =>
  `${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;

const emptyRow = () => ({
  id: generateID(),
  field: "",
  operator: "",
  value: "",
  value2: ""
});

const getDefaultQueriesForTab = currentTab => {
  if (currentTab === TAB_NAMES.MEMBERS) {
    return {
      filter: {
        status: [
          {
            operator: FILTER_OPERATIONS.EQUAL,
            value: MEMBER_STATES.ACTIVE
          }
        ]
      }
    };
  }
  if (currentTab === TAB_NAMES.APPLICATION_REQUESTS) {
    return {
      filter: {
        status: [
          {
            operator: FILTER_OPERATIONS.NOT_EQUAL,
            value: MEMBER_STATES.ACTIVE
          },
          {
            operator: FILTER_OPERATIONS.NOT_EQUAL,
            value: MEMBER_STATES.INACTIVE
          }
        ]
      }
    };
  }
  if (currentTab === TAB_NAMES.INACTIVE_MEMBERS) {
    return {
      filter: {
        status: [
          {
            operator: FILTER_OPERATIONS.EQUAL,
            value: MEMBER_STATES.INACTIVE
          }
        ]
      }
    };
  }
  if (currentTab === TAB_NAMES.KIDS) {
    return { filter: {} };
  }
  return {};
};

const isProtectedTabFilter = (currentTab, field, persistedFilter) => {
  if (field !== "status") return false;
  const defaults = getDefaultQueriesForTab(currentTab)?.filter?.status;
  if (!defaults) return false;
  return defaults.some(
    d =>
      d.operator === persistedFilter.operator &&
      d.value === persistedFilter.value
  );
};

const rowsFromQueries = (queries, currentTab) => {
  const persisted = queries?.filter || {};
  const rows = [];
  Object.entries(persisted).forEach(([field, entries]) => {
    (entries || []).forEach(entry => {
      if (isProtectedTabFilter(currentTab, field, entry)) return;
      const isRange =
        entry.operator === FILTER_OPERATIONS.BETWEEN &&
        Array.isArray(entry.value);
      rows.push({
        id: generateID(),
        field,
        operator: entry.operator,
        value: isRange ? entry.value[0] ?? "" : entry.value ?? "",
        value2: isRange ? entry.value[1] ?? "" : ""
      });
    });
  });
  return rows.length ? rows : [emptyRow()];
};

const buildAppliedFilters = (rows, currentTab) => {
  const applied = {};

  const defaults = getDefaultQueriesForTab(currentTab)?.filter || {};
  Object.entries(defaults).forEach(([field, entries]) => {
    applied[field] = [...entries];
  });

  rows.forEach(row => {
    if (!row.field || !row.operator) return;

    let entry;
    if (VALUELESS_OPERATORS.has(row.operator)) {
      entry = { operator: row.operator };
    } else if (row.operator === FILTER_OPERATIONS.BETWEEN) {
      if (row.value === "" || row.value2 === "") return;
      entry = { operator: row.operator, value: [row.value, row.value2] };
    } else {
      if (row.value === "" || row.value === undefined) return;
      entry = { operator: row.operator, value: row.value };
    }

    if (!applied[row.field]) applied[row.field] = [];
    applied[row.field].push(entry);
  });

  return applied;
};

const LOCATION_LEVELS = [
  "country",
  "region",
  "zone",
  "city",
  "sub_city",
  "woreda"
];

const LocationCascadeInput = ({ level, value, onChange }) => {
  const { t } = useTranslation();
  const targetIdx = LOCATION_LEVELS.indexOf(level);

  const [countryId, setCountryId] = useState(
    level === "country" ? value || "" : ""
  );
  const [regionId, setRegionId] = useState(
    level === "region" ? value || "" : ""
  );
  const [zoneId, setZoneId] = useState(level === "zone" ? value || "" : "");
  const [cityId, setCityId] = useState(level === "city" ? value || "" : "");
  const [subCityId, setSubCityId] = useState(
    level === "sub_city" ? value || "" : ""
  );

  const countries = useCountries();
  const regions = useRegions({
    countryId,
    config: { enabled: !!countryId && targetIdx >= 1 }
  });
  const zones = useZones({
    regionId,
    config: { enabled: !!regionId && targetIdx >= 2 }
  });
  const cities = useCities({
    zoneId,
    config: { enabled: !!zoneId && targetIdx >= 3 }
  });
  const subcities = useSubcities({
    cityId,
    config: { enabled: !!cityId && targetIdx >= 4 }
  });
  const woredas = useWoredas({
    subcityId: subCityId,
    config: { enabled: !!subCityId && targetIdx >= 5 }
  });

  const optionsFor = result =>
    (result?.data?.result || []).map(item => ({
      optionLabel: item.name,
      value: String(item.id)
    }));

  const renderSelect = (
    placeholder,
    opts,
    currentVal,
    handler,
    disabled,
    loading
  ) => (
    <TextField
      select
      size="small"
      sx={{
        minWidth: 140,
        flex: 1,
        "& .MuiInputBase-root.Mui-disabled": {
          backgroundColor: "transparent"
        }
      }}
      value={currentVal || ""}
      onChange={handler}
      disabled={disabled || loading}
      SelectProps={{
        displayEmpty: true,
        renderValue: selected => {
          if (!selected) {
            return (
              <Typography variant="body2" color="text.disabled">
                {loading
                  ? t("members.filterBuilder.loadingLocation", {
                      level: placeholder.toLowerCase()
                    })
                  : placeholder}
              </Typography>
            );
          }
          const match = opts.find(o => o.value === selected);
          return match?.optionLabel ?? selected;
        }
      }}
    >
      {opts.map(o => (
        <MenuItem key={o.value} value={o.value}>
          {o.optionLabel}
        </MenuItem>
      ))}
    </TextField>
  );

  const resultsByLevel = {
    country: countries,
    region: regions,
    zone: zones,
    city: cities,
    sub_city: subcities,
    woreda: woredas
  };

  const findLabel = (lvl, id) => {
    const result = resultsByLevel[lvl]?.data?.result || [];
    const match = result.find(item => String(item.id) === String(id));
    return match?.name || "";
  };

  const setLevel = (lvl, val) => {
    if (lvl === "country") {
      setCountryId(val);
      setRegionId("");
      setZoneId("");
      setCityId("");
      setSubCityId("");
    } else if (lvl === "region") {
      setRegionId(val);
      setZoneId("");
      setCityId("");
      setSubCityId("");
    } else if (lvl === "zone") {
      setZoneId(val);
      setCityId("");
      setSubCityId("");
    } else if (lvl === "city") {
      setCityId(val);
      setSubCityId("");
    } else if (lvl === "sub_city") {
      setSubCityId(val);
    }
    if (lvl === level) onChange(val, val ? findLabel(lvl, val) : "");
    else onChange("", "");
  };

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {targetIdx >= 0 &&
        renderSelect(
          t(LOCATION_LEVEL_LABEL_KEYS.country),
          optionsFor(countries),
          countryId,
          e => setLevel("country", e.target.value),
          false,
          countries.isFetching
        )}
      {targetIdx >= 1 &&
        renderSelect(
          t(LOCATION_LEVEL_LABEL_KEYS.region),
          optionsFor(regions),
          regionId,
          e => setLevel("region", e.target.value),
          !countryId,
          regions.isFetching
        )}
      {targetIdx >= 2 &&
        renderSelect(
          t(LOCATION_LEVEL_LABEL_KEYS.zone),
          optionsFor(zones),
          zoneId,
          e => setLevel("zone", e.target.value),
          !regionId,
          zones.isFetching
        )}
      {targetIdx >= 3 &&
        renderSelect(
          t(LOCATION_LEVEL_LABEL_KEYS.city),
          optionsFor(cities),
          cityId,
          e => setLevel("city", e.target.value),
          !zoneId,
          cities.isFetching
        )}
      {targetIdx >= 4 &&
        renderSelect(
          t(LOCATION_LEVEL_LABEL_KEYS.sub_city),
          optionsFor(subcities),
          subCityId,
          e => setLevel("sub_city", e.target.value),
          !cityId,
          subcities.isFetching
        )}
      {targetIdx >= 5 &&
        renderSelect(
          t(LOCATION_LEVEL_LABEL_KEYS.woreda),
          optionsFor(woredas),
          value || "",
          e =>
            onChange(
              e.target.value,
              e.target.value ? findLabel("woreda", e.target.value) : ""
            ),
          !subCityId,
          woredas.isFetching
        )}
    </Stack>
  );
};

export function FilterMembers() {
  const { t } = useTranslation();
  const setHeaderTitle = useCentralStore(state => state.setHeaderTitle);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const setQueries = useMembersStore(state => state.setQueries);
  const queries = useMembersStore(state => state.queries);
  const openExportModal = useMembersStore(state => state.openExportModal);
  const currentTab = useMembersStore(state => state.currentTab);
  const addRecipient = useMessagesStore(state => state.addRecipient);
  const clearRecipients = useMessagesStore(state => state.clearRecipients);
  const addMember = useMessagesStore(state => state.addMember);
  const removeMember = useMessagesStore(state => state.removeMember);
  const navigate = useNavigate();
  const openAddTeamModal = useMembersStore(state => state.openAddTeamModal);
  const closeAddTeamModal = useMembersStore(state => state.closeAddTeamModal);
  const addTeamMember = useAddTeamMember();
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const location = useLocation();
  const [locationLabels, setLocationLabels] = useState({});

  const setLocationLabel = (field, value, label) => {
    if (!value) return;
    setLocationLabels(prev => ({ ...prev, [`${field}-${value}`]: label }));
  };

  const fieldSchema = useMemo(() => {
    let schema = {};
    if (
      currentTab === TAB_NAMES.MEMBERS ||
      currentTab === TAB_NAMES.APPLICATION_REQUESTS ||
      currentTab === TAB_NAMES.INACTIVE_MEMBERS
    ) {
      schema = membersFilterFields;
    } else if (currentTab === TAB_NAMES.KIDS) schema = kidsFilterFields;
    else if (currentTab === TAB_NAMES.PARTNERS) schema = partnersFilterFields;
    return resolveFieldOptions(schema, t);
  }, [currentTab, t]);

  const fieldFlatList = useMemo(
    () =>
      Object.entries(fieldSchema).map(([key, def]) => ({
        value: key,
        label: def.label || key,
        category: def.category || "Other"
      })),
    [fieldSchema]
  );

  const rowStatus = row => {
    if (!row.field) return "empty";
    if (!row.operator) return "needs-operator";
    if (VALUELESS_OPERATORS.has(row.operator)) return "valid";
    if (row.operator === FILTER_OPERATIONS.BETWEEN) {
      return row.value !== "" && row.value2 !== "" ? "valid" : "needs-value";
    }
    return row.value !== "" && row.value !== undefined
      ? "valid"
      : "needs-value";
  };

  const [rows, setRows] = useState(() => {
    const queryParams = queryString.parse(location.search);
    if (queryParams.queries) {
      try {
        return rowsFromQueries(JSON.parse(queryParams.queries), currentTab);
      } catch {
        /* fall through */
      }
    }
    return rowsFromQueries(queries, currentTab);
  });

  useEffect(() => {
    setHeaderTitle(t("members.filterBuilder.headerTitle"));
    return () => setHeaderTitle("");
  }, [setHeaderTitle, t]);

  const updateRow = (id, patch) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = id => {
    setRows(prev => {
      const next = prev.filter(r => r.id !== id);
      return next.length ? next : [emptyRow()];
    });
  };

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const handleFieldChange = (id, nextField) => {
    const def = fieldSchema[nextField];
    const allowed = getOperatorsForField(def);
    const defaultOp =
      def?.type === "string"
        ? FILTER_OPERATIONS.LIKE
        : allowed[0] || FILTER_OPERATIONS.EQUAL;
    updateRow(id, {
      field: nextField,
      operator: defaultOp,
      value: "",
      value2: ""
    });
  };

  const handleOperatorChange = (id, nextOp) =>
    updateRow(id, { operator: nextOp, value: "", value2: "" });

  const applyRows = nextRows => {
    const applied = buildAppliedFilters(nextRows, currentTab);
    setQueries({ ...(queries || {}), filter: applied });
  };

  const handleApply = () => applyRows(rows);

  const handleClear = () => {
    const fresh = [emptyRow()];
    setRows(fresh);
    setQueries(getDefaultQueriesForTab(currentTab));
  };

  const removeAppliedFilter = (field, idx) => {
    const current = queries?.filter || {};
    const next = { ...current };
    const entries = (next[field] || []).filter((_, i) => i !== idx);
    if (entries.length) next[field] = entries;
    else delete next[field];
    setQueries({ ...(queries || {}), filter: next });
    setRows(rowsFromQueries({ ...(queries || {}), filter: next }, currentTab));
  };

  const renderValueInput = row => {
    const def = fieldSchema[row.field];
    if (!def || VALUELESS_OPERATORS.has(row.operator)) return null;
    const isBetween = row.operator === FILTER_OPERATIONS.BETWEEN;

    if (def.type === "date") {
      return (
        <Stack direction="row" spacing={1}>
          <EthiopianDatePicker
            width="100%"
            height={40}
            borderRadius={4}
            label={isBetween ? t("members.filterBuilder.from") : ""}
            value={row.value || ""}
            onChange={e => updateRow(row.id, { value: e.target.value })}
          />
          {isBetween && (
            <EthiopianDatePicker
              width="100%"
              height={40}
              borderRadius={4}
              label={t("members.filterBuilder.to")}
              value={row.value2 || ""}
              onChange={e => updateRow(row.id, { value2: e.target.value })}
            />
          )}
        </Stack>
      );
    }

    if (def.type === "enum") {
      return (
        <TextField
          select
          size="small"
          fullWidth
          value={row.value || ""}
          onChange={e => updateRow(row.id, { value: e.target.value })}
          SelectProps={{
            displayEmpty: true,
            renderValue: selected => {
              if (!selected) {
                return (
                  <Typography variant="body2" color="text.disabled">
                    {t("members.filterBuilder.selectValue")}
                  </Typography>
                );
              }
              const opt = (def.options || []).find(o => o.value === selected);
              return opt?.label ?? selected;
            }
          }}
        >
          {(def.options || []).map(o => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (def.type === "location") {
      return (
        <LocationCascadeInput
          key={row.id}
          level={def.locationLevel}
          value={row.value}
          onChange={(val, label) => {
            updateRow(row.id, { value: val });
            setLocationLabel(row.field, val, label);
          }}
        />
      );
    }

    if (def.type === "number") {
      return (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            fullWidth
            type="number"
            placeholder={
              isBetween
                ? t("members.filterBuilder.from")
                : t("members.filterBuilder.value")
            }
            value={row.value}
            onChange={e => updateRow(row.id, { value: e.target.value })}
          />
          {isBetween && (
            <TextField
              size="small"
              fullWidth
              type="number"
              placeholder={t("members.filterBuilder.to")}
              value={row.value2}
              onChange={e => updateRow(row.id, { value2: e.target.value })}
            />
          )}
        </Stack>
      );
    }

    return (
      <TextField
        size="small"
        fullWidth
        type="text"
        placeholder={t("members.filterBuilder.value")}
        value={row.value}
        onChange={e => updateRow(row.id, { value: e.target.value })}
      />
    );
  };

  const messageSelections = () => {
    clearRecipients();
    for (const member of selectedMembers) {
      removeMember(member.id);
      addRecipient(
        `${member.id}_${member?.first_name}_${member?.last_name}`,
        member
      );
      addMember(member.id, { id: member.id });
      navigate("/messages");
    }
  };

  const groupSelections = teamId => {
    const selectedMembersId = selectedMembers.map(m => m.id);
    addTeamMember.mutate(
      {
        churchId: selectedChurch?.id,
        teamId: teamId,
        memberData: { member_ids: selectedMembersId }
      },
      {
        onSuccess: () => {
          setSelectedMembers([]);
          closeAddTeamModal();
        }
      }
    );
  };

  const activeChips = useMemo(() => {
    const applied = queries?.filter || {};
    const chips = [];
    Object.entries(applied).forEach(([field, entries]) => {
      (entries || []).forEach((entry, idx) => {
        if (isProtectedTabFilter(currentTab, field, entry)) return;
        const def = fieldSchema[field];
        const fieldLabel = def?.label || field;
        const opLabel = OPERATOR_LABEL_KEYS[entry.operator]
          ? t(OPERATOR_LABEL_KEYS[entry.operator])
          : entry.operator;
        let valueLabel = "";
        if (!VALUELESS_OPERATORS.has(entry.operator)) {
          if (Array.isArray(entry.value)) {
            valueLabel = `${entry.value[0]} – ${entry.value[1]}`;
          } else if (def?.type === "enum") {
            const opt = (def.options || []).find(o => o.value === entry.value);
            valueLabel = opt?.label ?? entry.value;
          } else if (def?.type === "location") {
            valueLabel =
              locationLabels[`${field}-${entry.value}`] ?? entry.value;
          } else {
            valueLabel = entry.value;
          }
        }
        chips.push({
          key: `${field}-${idx}-${entry.operator}`,
          label: valueLabel
            ? `${fieldLabel} ${opLabel} ${valueLabel}`
            : `${fieldLabel} ${opLabel}`,
          field,
          idx
        });
      });
    });
    return chips;
  }, [queries, fieldSchema, currentTab, locationLabels, t]);

  const operatorOptions = row => {
    const def = fieldSchema[row.field];
    const ops = getOperatorsForField(def);
    return ops.map(op => ({
      optionLabel: OPERATOR_LABEL_KEYS[op] ? t(OPERATOR_LABEL_KEYS[op]) : op,
      value: op
    }));
  };

  const validCount = useMemo(
    () => rows.filter(r => rowStatus(r) === "valid").length,
    [rows]
  );

  const hasChanges = useMemo(() => {
    const draft = buildAppliedFilters(rows, currentTab);
    return !isEqual(draft, queries?.filter || {});
  }, [rows, queries, currentTab]);

  const isEmpty = rows.length === 1 && rowStatus(rows[0]) === "empty";

  return (
    <Box sx={{ pt: 6 }}>
      <Notification />
      <AddTeamModal onSelect={teamId => groupSelections(teamId)} />
      <ColumnsVisibilityModal />
      <ExportModal />
      <Stack spacing={2}>
        <MembersTableHeader isFilterMode={true} />

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            mb={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Funnel size={20} />
              <Typography variant="subtitle1" fontWeight={600}>
                {t("members.filterBuilder.buildFilter")}
              </Typography>
              {validCount > 0 && (
                <Chip
                  size="small"
                  label={t("members.filterBuilder.conditionCount", {
                    count: validCount
                  })}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>

          <Stack spacing={1}>
            {rows.map((row, index) => {
              const def = fieldSchema[row.field];
              const status = rowStatus(row);
              const selectedField =
                fieldFlatList.find(o => o.value === row.field) || null;
              return (
                <Fragment key={row.id}>
                  {index > 0 && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pl: 5, pr: 6 }}
                    >
                      <Divider sx={{ flex: 1 }} />
                      <Chip
                        label={t("members.filterBuilder.and")}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: 11,
                          letterSpacing: 0.5,
                          height: 20
                        }}
                      />
                      <Divider sx={{ flex: 1 }} />
                    </Stack>
                  )}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "stretch", md: "flex-start" }}
                    sx={{
                      borderRadius: 1.5,
                      border: theme =>
                        status === "needs-value" || status === "needs-operator"
                          ? `1px dashed ${theme.palette.warning.main}`
                          : "1px solid transparent",
                      p: 1,
                      transition: "border-color 0.15s ease"
                    }}
                  >
                    <Stack
                      spacing={0.5}
                      sx={{ flexShrink: 0, alignSelf: "stretch" }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ visibility: "hidden" }}
                      >
                        .
                      </Typography>
                      <Box
                        sx={{
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 700
                          }}
                        >
                          {index + 1}
                        </Box>
                      </Box>
                    </Stack>

                    <Stack spacing={0.5} sx={{ minWidth: 240, flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {t("members.filterBuilder.field")}
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={fieldFlatList}
                        groupBy={opt => opt.category}
                        getOptionLabel={opt => opt.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={selectedField}
                        onChange={(_, opt) =>
                          handleFieldChange(row.id, opt?.value || "")
                        }
                        renderInput={params => (
                          <TextField
                            {...params}
                            placeholder={t("members.filterBuilder.searchField")}
                          />
                        )}
                      />
                    </Stack>

                    <Stack spacing={0.5} sx={{ minWidth: 180 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {t("members.filterBuilder.condition")}
                      </Typography>
                      <TextField
                        select
                        size="small"
                        value={row.operator || ""}
                        onChange={e =>
                          handleOperatorChange(row.id, e.target.value)
                        }
                        disabled={!def}
                        sx={{
                          "& .MuiInputBase-root.Mui-disabled": {
                            backgroundColor: "transparent"
                          }
                        }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: selected => {
                            if (!selected) {
                              return (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  {def
                                    ? t("members.filterBuilder.selectCondition")
                                    : t("members.filterBuilder.pickFieldFirst")}
                                </Typography>
                              );
                            }
                            return OPERATOR_LABEL_KEYS[selected]
                              ? t(OPERATOR_LABEL_KEYS[selected])
                              : selected;
                          }
                        }}
                      >
                        {operatorOptions(row).map(op => (
                          <MenuItem key={op.value} value={op.value}>
                            {op.optionLabel}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>

                    <Stack spacing={0.5} sx={{ flex: 2, minWidth: 220 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {t("members.filterBuilder.value")}
                      </Typography>
                      {renderValueInput(row) || (
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            minHeight: 40,
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.disabled"
                            fontStyle="italic"
                          >
                            {!row.field
                              ? t("members.filterBuilder.pickFieldForValue")
                              : VALUELESS_OPERATORS.has(row.operator)
                              ? t("members.filterBuilder.noValueNeeded")
                              : t("members.filterBuilder.pickCondition")}
                          </Typography>
                        </Box>
                      )}
                      {status === "needs-value" && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ color: "warning.main", pl: 0.5 }}
                        >
                          <Warning size={12} />
                          <Typography variant="caption">
                            {t("members.filterBuilder.enterValueWarning")}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>

                    <Stack
                      spacing={0.5}
                      sx={{ flexShrink: 0, alignSelf: "stretch" }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ visibility: "hidden" }}
                      >
                        .
                      </Typography>
                      <Box
                        sx={{
                          height: 40,
                          display: "flex",
                          alignItems: "center"
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => removeRow(row.id)}
                          sx={{
                            color: "text.secondary",
                            "&:hover": { color: "error.main" }
                          }}
                          aria-label={t(
                            "members.filterBuilder.removeCondition"
                          )}
                        >
                          <Trash size={18} />
                        </IconButton>
                      </Box>
                    </Stack>
                  </Stack>
                </Fragment>
              );
            })}
          </Stack>

          {isEmpty && (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("members.filterBuilder.emptyState")}
              </Typography>
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Button
              variant="contained"
              color="secondary"
              flavor="outlined"
              onClick={addRow}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Plus size={16} />
                <span>{t("members.filterBuilder.addCondition")}</span>
              </Stack>
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="secondary"
                flavor="outlined"
                onClick={handleClear}
              >
                {t("members.filterBuilder.clearAll")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApply}
                disabled={!hasChanges}
              >
                {validCount > 0
                  ? t("members.filterBuilder.applyCount", {
                      count: validCount
                    })
                  : t("members.filterBuilder.apply")}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {activeChips.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="body2" color="text.secondary" mr={0.5}>
              {t("members.filterBuilder.activeFilters")}
            </Typography>
            {activeChips.map(chip => (
              <Chip
                key={chip.key}
                label={chip.label}
                onDelete={() => removeAppliedFilter(chip.field, chip.idx)}
                deleteIcon={<X size={14} />}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={1}>
          <Button variant="contained" color="primary" onClick={openExportModal}>
            {t("members.members.exportMember")}
          </Button>
          {selectedMembers.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={openAddTeamModal}
            >
              {t("members.filterBuilder.groupSelection")}
            </Button>
          )}
          {selectedMembers.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={messageSelections}
            >
              {t("members.filterBuilder.messageSelection")}
            </Button>
          )}
        </Stack>
        {currentTab === TAB_NAMES.MEMBERS && (
          <MembersTable
            isFilterMode={true}
            selectedMembers={selectedMembers}
            setSelectedMembers={setSelectedMembers}
          />
        )}
        {currentTab === TAB_NAMES.APPLICATION_REQUESTS && (
          <ApplicationRequestsTable isFilterMode={true} />
        )}
        {currentTab === TAB_NAMES.INACTIVE_MEMBERS && (
          <InactiveMembersTable isFilterMode={true} />
        )}
        {currentTab === TAB_NAMES.KIDS && <KidsTable isFilterMode={true} />}
        {currentTab === TAB_NAMES.PARTNERS && (
          <PartnersTable isFilterMode={true} />
        )}
      </Stack>
    </Box>
  );
}
